import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import {
  Bot,
  Plus,
  Edit,
  Trash,
  Play,
  Copy,
  CheckCircle,
  XCircle,
  Clock,
  MessageSquare,
  Phone,
  Mail,
  BarChart3,
} from 'lucide-react';
// import AgentBuilder from '../components/AgentBuilder';

const AGENT_API_URL = 'https://ncai-agents.bill-burkey.workers.dev/api';

export default function Agents() {
  const { organization } = useAuth();
  const queryClient = useQueryClient();
  const [_showBuilder, _setShowBuilder] = useState(false);
  const [_selectedAgent, _setSelectedAgent] = useState<any>(null);
  const [testingAgent, setTestingAgent] = useState<string | null>(null);

  // Fetch agents
  const { data: agentsData, isLoading } = useQuery({
    queryKey: ['agents', organization?.id],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${AGENT_API_URL}/agents?organizationId=${organization?.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await response.json();
      return data.data;
    },
    enabled: !!organization,
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (agentId: string) => {
      const token = localStorage.getItem('token');
      const response = await fetch(`${AGENT_API_URL}/agents/${agentId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agents'] });
    },
  });

  // Duplicate mutation
  const duplicateMutation = useMutation({
    mutationFn: async (agent: any) => {
      const token = localStorage.getItem('token');
      const response = await fetch(`${AGENT_API_URL}/agents`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...agent,
          name: `${agent.name} (Copy)`,
          status: 'draft',
        }),
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agents'] });
    },
  });

  const agents = agentsData?.agents || [];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'text-green-600 bg-green-100';
      case 'draft':
        return 'text-gray-600 bg-gray-100';
      case 'inactive':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-4 h-4" />;
      case 'draft':
        return <Clock className="w-4 h-4" />;
      case 'inactive':
        return <XCircle className="w-4 h-4" />;
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">AI Agents</h1>
          <p className="text-gray-500 mt-1">Create and manage custom AI agents</p>
        </div>
        <button
          onClick={() => {
            _setSelectedAgent(null);
            _setShowBuilder(true);
          }}
          className="btn btn-primary flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Create Agent
        </button>
      </div>

      {agents.length === 0 ? (
        <div className="card p-12 text-center">
          <Bot className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No agents yet</h3>
          <p className="text-gray-500 mb-6">
            Create your first AI agent to handle conversations across multiple channels
          </p>
          <button
            onClick={() => _setShowBuilder(true)}
            className="btn btn-primary mx-auto"
          >
            <Plus className="w-5 h-5 mr-2" />
            Create Your First Agent
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {agents.map((agent: any) => (
            <div key={agent.id} className="card p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Bot className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{agent.name}</h3>
                    <div className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full mt-1 ${getStatusColor(agent.status)}`}>
                      {getStatusIcon(agent.status)}
                      <span className="capitalize">{agent.status}</span>
                    </div>
                  </div>
                </div>
              </div>

              <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                {agent.description || agent.personality}
              </p>

              <div className="flex items-center gap-2 mb-4">
                {agent.can_chat && (
                  <div className="flex items-center gap-1 text-xs bg-gray-100 px-2 py-1 rounded">
                    <MessageSquare className="w-3 h-3" />
                    Chat
                  </div>
                )}
                {agent.can_phone && (
                  <div className="flex items-center gap-1 text-xs bg-gray-100 px-2 py-1 rounded">
                    <Phone className="w-3 h-3" />
                    Phone
                  </div>
                )}
                {agent.can_email && (
                  <div className="flex items-center gap-1 text-xs bg-gray-100 px-2 py-1 rounded">
                    <Mail className="w-3 h-3" />
                    Email
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                <div className="flex items-center gap-1">
                  <BarChart3 className="w-4 h-4" />
                  {agent.active_deployments || 0} active
                </div>
                <div>v{agent.version || 1}</div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setTestingAgent(agent.id)}
                  className="btn btn-secondary flex-1 text-sm py-2"
                  title="Test Agent"
                >
                  <Play className="w-4 h-4" />
                </button>
                <button
                  onClick={() => {
                    _setSelectedAgent(agent);
                    _setShowBuilder(true);
                  }}
                  className="btn btn-secondary flex-1 text-sm py-2"
                  title="Edit Agent"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={() => duplicateMutation.mutate(agent)}
                  className="btn btn-secondary text-sm py-2"
                  title="Duplicate"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <button
                  onClick={() => {
                    if (confirm('Are you sure you want to delete this agent?')) {
                      deleteMutation.mutate(agent.id);
                    }
                  }}
                  className="btn btn-secondary text-sm py-2 text-red-600"
                  title="Delete"
                >
                  <Trash className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Agent Builder Modal - Coming Soon */}
      {/* {showBuilder && (
        <AgentBuilder
          agent={selectedAgent}
          organizationId={organization?.id || ''}
          onClose={() => {
            setShowBuilder(false);
            setSelectedAgent(null);
          }}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['agents'] });
            setShowBuilder(false);
            setSelectedAgent(null);
          }}
        />
      )} */}

      {/* Test Agent Modal */}
      {testingAgent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full">
            <h2 className="text-xl font-bold mb-4">Test Agent</h2>
            <p className="text-gray-600 mb-4">
              Send test messages to see how your agent responds
            </p>
            <div className="bg-gray-50 rounded-lg p-4 mb-4" style={{ minHeight: '300px' }}>
              <p className="text-center text-gray-500">Chat interface coming soon...</p>
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => setTestingAgent(null)}
                className="btn btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
